import { format } from 'date-fns';
import * as Clipboard from 'expo-clipboard';
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  FlatList,
  ListRenderItemInfo,
  TouchableOpacity,
  Image,
  TextInput,
  Platform,
  UIManager,
} from 'react-native';
import { useToast } from 'react-native-toast-notifications';

import Avatar from '@components/Avatar/Avatar';
import { EmailAvatar } from '@components/Avatar/EmailAvatar';
import Highlighter from '@components/HighlightWords/index';
import { Box, Text } from '@components/Restyle/index';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { WebIcon } from '@components/shared/Icon/WebIcon';
import { AddMembersInput } from '@components/TaskList/AnimatedSearchBox.web';
import { BaseButtonV3 } from '@components/TaskList/BaseButtonV3';
import { SimpleHoverable } from '@components/TaskList/SimpleHoverable';
import { TaskCreatedToast } from '@components/TaskList/TaskCreatedToast.web';
import { TaskRoleSelectorWeb } from '@components/TaskList/TaskRoleSelector.web';
import {
  User,
  TaskMemberRole,
  Group,
  useCreateInviteMutation,
  WebTutorialFlow,
  useCompleteWebTutorialFlowsMutation,
  Scalars,
  Task,
  useUpdateTaskMutation,
  useUpdateTaskMembersMutation,
  useCreateTaskInvitationMutation,
  useDeleteTaskInvitationMutation,
  TaskPriority,
  TaskStatus,
  useDeleteUserRequestMutation,
} from '@graphql/generated';
import { useContacts } from '@hooks/useContacts';
import { useGetTasksFromCache } from '@hooks/useGetTasksFromCache';
import useMe from '@hooks/useMe';
import { useMemberSearch } from '@hooks/useMemberSearch';
import { getTaskMembership } from '@hooks/useTaskMembership';
import documentIcons from '@themes/documentIcons';
import Images from '@themes/images';
import theme from '@themes/theme';
import { getSuggestionEmail } from '@utils/emailSuggestion';
import { localDateFromUTCDate } from '@utils/formatters/date';
import { getSubmitErrorMessage } from '@utils/getSubmitErrorMessage';
import { mutationFailed, toMutationError } from '@utils/graphqlError';
import { buildMembersAfterRemoval } from '@utils/taskMemberUtils';

// Enable LayoutAnimation on Android
if (
  Platform.OS === 'android' &&
  UIManager.setLayoutAnimationEnabledExperimental
) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

export type StringSearchType = {
  id?: string;
  title: string;
  role: TaskMemberRole;
  expiredAt?: Scalars['ISO8601DateTime'];
  __typename: string;
  isTaskTagUser: boolean;
};

export type UserSearchType =
  | (Pick<
      User,
      | 'id'
      | 'name'
      | 'firstName'
      | 'lastName'
      | 'email'
      | 'avatar'
      | '__typename'
    > & { role: TaskMemberRole; isTaskTagUser: boolean })
  | StringSearchType;

interface TaskInviteOrAssigneeProps {
  selectedUser?: UserSearchType[];
  onAssignMember?: (members: UserSearchType[]) => void;
  onClose?: () => void;
  taskIdList?: string[];
  isProjectEditor?: boolean;
  isTeamMember?: boolean;
}

// Selected group type used in the assign panel
export type SelectedGroupType = Group & { users?: UserSearchType[] };

const getDisplayName = (u: User) => {
  const { name, firstName, lastName } = u;
  return name ?? `${firstName ?? ''} ${lastName ?? ''}`.trim();
};

const SearchGroupMemberItem: React.FC<{
  gu: User;
  isAlreadyAdded: boolean;
  onPress: () => void;
  renderUser: (user: any) => React.ReactNode;
  alreadyAddedLabel: string;
}> = ({ gu, isAlreadyAdded, onPress, renderUser, alreadyAddedLabel }) => (
  <SimpleHoverable
    accessibilityLabel={`Add Assignee | ${getDisplayName(gu)}`}
    onPress={onPress}>
    {(hover1) => (
      <Box
        flex={1}
        pr='m'
        py='s'
        alignItems='center'
        flexDirection='row'
        borderBottomColor='grey02'
        borderBottomWidth={1}
        style={{
          backgroundColor: hover1 ? '#FAFBFC' : 'white',
          paddingLeft: 64,
        }}
        justifyContent='center'>
        <Box flex={1}>{renderUser(gu)}</Box>
        {isAlreadyAdded && (
          <Text
            flexShrink={1}
            variant='webBodyHintSubline'
            color='grey04'
            marginTop='xxs'
            ellipsizeMode='tail'
            numberOfLines={1}>
            {alreadyAddedLabel}
          </Text>
        )}
      </Box>
    )}
  </SimpleHoverable>
);

const SearchGroupRow: React.FC<{
  group: Group;
  isExpanded: boolean;
  searchVal: string;
  selectedUser: any[];
  onToggleExpand: () => void;
  onSelectGroup: () => void;
  onSelectMember: (gu: User) => void;
  renderUser: (user: any) => React.ReactNode;
  alreadyAddedLabel: string;
}> = ({
  group,
  isExpanded,
  searchVal,
  selectedUser,
  onToggleExpand,
  onSelectGroup,
  onSelectMember,
  renderUser,
  alreadyAddedLabel,
}) => (
  <SimpleHoverable accessibilityLabel={`Add Assignee Group | ${group.name}`}>
    {(hover) => (
      <Box>
        <Box
          flex={1}
          px='m'
          py='s'
          borderBottomColor='grey02'
          borderBottomWidth={1}
          style={{ backgroundColor: hover ? '#FAFBFC' : 'white' }}
          justifyContent='center'>
          <Box flex={1} flexDirection='row' alignItems='center'>
            <TouchableOpacity
              accessibilityLabel={`${
                isExpanded ? 'Collapse' : 'Expand'
              } Group | ${group.name}`}
              onPress={onToggleExpand}>
              <Icon
                name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                variant='l'
                marginHorizontal='xs'
                color='grey05'
              />
            </TouchableOpacity>

            <TouchableOpacity
              accessibilityLabel={`Add Assignee Group | ${group.name}`}
              style={{ flex: 1 }}
              onPress={onSelectGroup}>
              <Box ml='xs'>
                <Text
                  variant='webBodySecondary'
                  color='textPrimary'
                  numberOfLines={1}>
                  <Highlighter
                    autoEscape
                    highlightStyle={{
                      backgroundColor: theme.colors.yellowBright,
                    }}
                    searchWords={[searchVal]}
                    textToHighlight={group.name}
                  />
                </Text>
                <Text
                  variant='webBodyHintSubline'
                  color='grey04'
                  marginTop='xxs'
                  numberOfLines={1}>
                  {`${(group.users ?? []).length} members`}
                </Text>
              </Box>
            </TouchableOpacity>
          </Box>
        </Box>

        {isExpanded &&
          (group.users ?? []).map((gu: User) => (
            <SearchGroupMemberItem
              key={`${group.id}:${gu.id}`}
              gu={gu}
              isAlreadyAdded={selectedUser.some((u) => u?.email === gu.email)}
              onPress={() => onSelectMember(gu)}
              renderUser={renderUser}
              alreadyAddedLabel={alreadyAddedLabel}
            />
          ))}
      </Box>
    )}
  </SimpleHoverable>
);

const AssignGroupMemberRow: React.FC<{
  member: any;
  groupId: string;
  canEditTaskTopLevel: boolean;
  isProjectEditor: boolean;
  isTeamMember: boolean;
  meId?: string;
  onRoleChange: (memberId: string, role: TaskMemberRole) => void;
  onRemove: (groupId: string, memberId: string) => void;
  renderUser: (user: any, showText?: boolean) => React.ReactNode;
}> = ({
  member,
  groupId,
  canEditTaskTopLevel,
  isProjectEditor,
  isTeamMember,
  meId,
  onRoleChange,
  onRemove,
  renderUser,
}) => {
  // TA-9491: editors and team members must be able to change roles too,
  // not just task owners.
  const canManageRoles = canEditTaskTopLevel || isProjectEditor || isTeamMember;
  const disabledRole =
    (member.role === TaskMemberRole.Assignee && !canManageRoles) ||
    member.id === meId;

  return (
    <SimpleHoverable
      accessibilityLabel={`Assignee | ${getDisplayName(member)}`}>
      {(hoverMember) => (
        <Box
          flex={1}
          px='xs'
          py='s'
          borderBottomColor='grey02'
          borderBottomWidth={1}
          flexDirection='row'
          backgroundColor={hoverMember ? 'grey01' : 'white'}
          alignItems='center'
          style={{ paddingLeft: 40 }}>
          <Box flex={1}>{renderUser(member, false)}</Box>

          <BaseButtonV3
            noBorder={true}
            noRadius={false}
            noHover={false}
            buttonRadius='xs'
            modalRadius={8}
            showHoverWhenDropdown={true}
            disabled={disabledRole}
            mr='s'
            popModalComponent={({ onClose: closeRoleModal }) => (
              <TaskRoleSelectorWeb
                isOwner={canEditTaskTopLevel}
                isProjectEditor={isProjectEditor}
                isTeamMember={isTeamMember}
                selectedRole={member.role}
                onRoleSelect={(role) => {
                  onRoleChange(member.id, role);
                  closeRoleModal();
                }}
              />
            )}>
            <Box
              width={86}
              mx='m'
              alignItems='center'
              justifyContent='flex-end'
              flexDirection='row'>
              <Text variant='webSmall' color='textPrimary' marginRight='xs'>
                {member.role === TaskMemberRole.Assignee
                  ? 'Assignee'
                  : 'Viewer'}
              </Text>
              {!disabledRole && (
                <Icon color='grey05' variant='s' name='ChevronDown' />
              )}
            </Box>
          </BaseButtonV3>

          {!disabledRole && member.id !== meId && (
            <GroupMemberTrashButton
              accessibilityLabel={`Remove Member | ${getDisplayName(member)}`}
              onPress={() => onRemove(groupId, member.id)}
            />
          )}
        </Box>
      )}
    </SimpleHoverable>
  );
};

const GroupMemberTrashButton: React.FC<{
  accessibilityLabel: string;
  onPress: () => void;
}> = ({ accessibilityLabel, onPress }) => (
  <SimpleHoverable accessibilityLabel={accessibilityLabel} onPress={onPress}>
    {(hoverTrash) => (
      <Box
        width={32}
        height={32}
        borderRadius='xs'
        backgroundColor={hoverTrash ? 'grey01' : 'white'}
        alignItems='center'
        justifyContent='center'>
        <Icon
          color={hoverTrash ? 'alertRed' : 'grey05'}
          variant='s'
          name='Trash2'
        />
      </Box>
    )}
  </SimpleHoverable>
);

export const TaskInviteOrAssignee = ({
  selectedUser = [],
  onClose,
  onAssignMember: onAssignMemberFromLastPage,
  taskIdList = [],
  isProjectEditor = false,
  isTeamMember = false,
}: TaskInviteOrAssigneeProps) => {
  const inputRef = useRef<TextInput>(null);
  const { t } = useTranslation();
  const { me } = useMe();
  const { contacts } = useContacts();

  const [isHoveredCopyLinkToInvite, setIsHoveredCopyLinkToInvite] =
    useState(false);

  const toast = useToast();

  // Pull task from cache only when taskId is present
  const { tasks } = useGetTasksFromCache(taskIdList);

  // Determine whether the current user can edit task top-level fields
  const canEditTaskTopLevel = useMemo(() => {
    if (tasks.length > 0) {
      return getTaskMembership(tasks[0], me?.id).canEditTaskTopLevel;
    }
    return true;
  }, [tasks, me?.id]);

  // TA-9491: project editors and team members are also allowed to assign,
  // not just task owners. Previously this used canEditTaskTopLevel only,
  // which forced every newly added user to a Viewer-only default role.
  const canManageRoles = canEditTaskTopLevel || isProjectEditor || isTeamMember;

  const defaultRole = canManageRoles
    ? TaskMemberRole.Assignee
    : TaskMemberRole.Viewer;

  const {
    searchVal,
    setText,
    focusOnSearch,
    setFocusOnSearch,
    shouldShowDropdown,
    listContacts,
    listGroup,
    listTasktagUsers,
    expandedGroupIds,
    searchExpandedGroupIds,
    toggleGroupExpanded,
    toggleSearchGroupExpanded,
    hasSearched,
  } = useMemberSearch({
    contacts,
    meId: me?.id,
    useLayoutAnimation: true,
  });

  // Keep local selected lists in sync with incoming props
  const initialSelectedUsers = useMemo(
    () =>
      selectedUser.filter((t) => t.__typename === 'User') as UserSearchType[],
    [selectedUser]
  );
  const initialSelectedEmails = useMemo(
    () =>
      selectedUser.filter((t) => t.__typename !== 'User') as StringSearchType[],
    [selectedUser]
  );

  // Selected standalone users (not belonging to a selected group)
  const [selectedUserList, setSelectedUserList] =
    useState<UserSearchType[]>(initialSelectedUsers);

  // Selected email items
  const [selectedEmailList, setSelectedEmailList] = useState<
    StringSearchType[]
  >(initialSelectedEmails);

  // Selected groups (UI only)
  const [selectedGroupList, setSelectedGroupList] = useState<
    SelectedGroupType[]
  >([]);

  // Keep latest values to avoid stale closures in callbacks
  const selectedUserListRef = useRef<UserSearchType[]>(selectedUserList);
  const selectedEmailListRef = useRef<StringSearchType[]>(selectedEmailList);
  const selectedGroupListRef = useRef<SelectedGroupType[]>(selectedGroupList);

  useEffect(() => {
    selectedUserListRef.current = selectedUserList;
  }, [selectedUserList]);

  useEffect(() => {
    selectedEmailListRef.current = selectedEmailList;
  }, [selectedEmailList]);

  useEffect(() => {
    selectedGroupListRef.current = selectedGroupList;
  }, [selectedGroupList]);

  useEffect(() => {
    setTimeout(() => {
      setFocusOnSearch(true);
    }, 200);
  }, []);

  const skipNextSyncRef = useRef(false);

  // A small helper to notify parent + mark this update as "internal"
  const notifyParent = useCallback(
    (members: UserSearchType[]) => {
      skipNextSyncRef.current = true;
      onAssignMemberFromLastPage?.(members);
    },
    [onAssignMemberFromLastPage]
  );
  // Build a merged "selected members" list to send back to parent
  // Includes: standalone users + group users + emails, and removes duplicates
  const buildMergedSelected = useCallback(
    (
      users: UserSearchType[],
      groups: SelectedGroupType[],
      emails: StringSearchType[]
    ) => {
      const usersFromGroups: UserSearchType[] = groups.flatMap(
        (g) => g.users ?? []
      );

      const merged: UserSearchType[] = [];
      const seen = new Set<string>();

      // Users (by user id)
      [...users, ...usersFromGroups].forEach((u: any) => {
        const id = u?.id;
        if (!id) return;
        const key = `user:${id}`;
        if (seen.has(key)) return;
        seen.add(key);
        merged.push(u);
      });

      // Emails (by lowercased title)
      emails.forEach((e) => {
        const title = (e.title || '').toLowerCase();
        if (!title) return;
        const key = `email:${title}`;
        if (seen.has(key)) return;
        seen.add(key);
        merged.push(e);
      });

      return merged;
    },
    []
  );
  useEffect(() => {
    // If the parent update is triggered by local actions, do NOT reset group UI state.
    if (skipNextSyncRef.current) {
      skipNextSyncRef.current = false;
      return;
    }

    // Sync when parent re-opens modal with different preselected items
    setSelectedUserList(initialSelectedUsers);
    setSelectedEmailList(initialSelectedEmails);

    // selectedGroupList is UI-only: clear only when this is a real external change
    setSelectedGroupList([]);
  }, [initialSelectedUsers, initialSelectedEmails]);

  const showToast = useCallback(
    (
      onPressCb: () => void,
      onCloseCb: () => void,
      title: string | undefined,
      msg: string | undefined,
      iconName?: keyof typeof Images | keyof typeof documentIcons,
      isFailedToast?: boolean
    ) => {
      return toast.show(
        <TaskCreatedToast
          title={title ?? 'Task Completed'}
          iconName={iconName}
          message={msg}
          duration={3000}
          onPress={onPressCb}
          isFailedToast={isFailedToast}
        />,
        {
          type: 'custom',
          duration: 3000,
          placement: 'bottom',
          animationType: 'slide-in',
          style: {
            marginBottom: 39,
            backgroundColor: 'transparent',
            shadowColor: 'transparent',
          },
          onClose: onCloseCb,
        }
      );
    },
    [toast]
  );

  const [updateTask] = useUpdateTaskMutation({});
  const [updateTaskMembers] = useUpdateTaskMembersMutation({});
  const [createTaskInvitation] = useCreateTaskInvitationMutation({});
  const [deleteTaskInvitation] = useDeleteTaskInvitationMutation();
  const [deleteUserRequest] = useDeleteUserRequestMutation();

  const paramValues = useCallback((item: Task) => {
    const startDate = item.startDate && localDateFromUTCDate(item.startDate);
    const dueDate = item.dueDate && localDateFromUTCDate(item.dueDate);

    return {
      name: item.name ?? '',
      description: item.description ?? '',
      fixedOrder: item?.fixedOrder ?? false,
      frequency: item?.frequency ?? undefined,
      dependencyIds: item.dependencies?.map((d) => d.id) || [],
      recurring: item?.recurring ?? false,
      projectId: item?.projectId ?? '0',
      phaseId: item?.phaseId ?? undefined,
      priority: item?.priority ?? TaskPriority.Low,
      status: item?.status ?? TaskStatus.Ongoing,
      skillIds: item?.skillIds ?? [],
      startDate,
      dueDate,
      completedAt: item?.completedAt ?? null,
    };
  }, []);

  const contactIdSet = useMemo(
    () => new Set((contacts ?? []).map((c) => c.id)),
    [contacts]
  );

  const onAssignMember = useCallback(
    async (list: UserSearchType[]) => {
      if (onAssignMemberFromLastPage) onAssignMemberFromLastPage(list);
      const taskList = tasks;
      const emailList = list.filter(
        (x) => x.__typename === 'StringSearchType'
      ) as StringSearchType[];

      const userList = list.filter(
        (x) => x.__typename !== 'StringSearchType' && !x.isTaskTagUser
      ) as UserSearchType[];

      const tasktagUserList = list.filter(
        (x) => x.__typename !== 'StringSearchType' && x.isTaskTagUser
      ) as UserSearchType[];

      if (
        emailList.length === 0 &&
        userList.length === 0 &&
        tasktagUserList.length === 0
      )
        return;

      const promises: Promise<any>[] = [];

      let hasRoleChanged = false;
      const taskById = new Map(taskList.map((task) => [task.id, task]));
      const missingTaskIds = taskIdList.filter(
        (taskId) => !taskById.has(taskId)
      );

      // Create invitations
      if (emailList.length > 0 || tasktagUserList.length > 0) {
        const fallbackInvitations = initialSelectedEmails.map((inv) => ({
          email: inv.title,
          role: inv.role,
        }));
        const fallbackUserRequests = initialSelectedUsers
          .filter((u) => u.__typename !== 'StringSearchType' && u.isTaskTagUser)
          .map((u) => ({
            role: u.role,
            user: {
              email: 'email' in u ? u.email : undefined,
            },
          }));
        const taskInvitationTargets = [
          ...taskList.map((task) => ({
            id: task.id,
            invitations: task.invitations ?? [],
            userRequests: task.userRequests ?? [],
          })),
          ...missingTaskIds.map((taskId) => ({
            id: taskId,
            invitations: fallbackInvitations,
            userRequests: fallbackUserRequests,
          })),
        ];

        for (const task of taskInvitationTargets) {
          const emailRoleMap = new Map<string, string>([
            ...emailList.map((e) => [e.title, e.role] as const),
            ...tasktagUserList.map((u) => [u?.email ?? u.id, u.role] as const),
          ]);
          if (!hasRoleChanged) {
            hasRoleChanged = task.invitations.some((inv) => {
              const nextRole = emailRoleMap.get(inv.email);
              return nextRole !== undefined && nextRole !== inv.role;
            });
            if (!hasRoleChanged) {
              hasRoleChanged = task.userRequests.some((inv) => {
                const nextRole = emailRoleMap.get(inv.user?.email ?? '');
                return nextRole !== undefined && nextRole !== inv.role;
              });
            }
          }
          const taskMembers = [
            ...emailList
              .filter(
                (ta) =>
                  !task.invitations.some(
                    (u) => u.email === ta.title && u.role === ta.role
                  )
              )
              .map((m) => ({
                email: m.title,
                role: m.role,
              })),
            ...tasktagUserList
              .filter(
                (ta) =>
                  !task.userRequests.some(
                    (ur) => ur.user?.email === ta.email && ur.role === ta.role
                  )
              )
              .map((m) => ({
                email: m.email,
                role: m.role,
              })),
          ];

          if (taskMembers.length > 0) {
            promises.push(
              createTaskInvitation({
                variables: {
                  taskId: task.id,
                  taskMembers,
                },
              })
            );
          }
        }
      }

      // Update members
      if (userList.length > 0) {
        for (const taskId of missingTaskIds) {
          promises.push(
            updateTaskMembers({
              variables: {
                id: taskId,
                attributes: userList.map((u) => ({
                  userId: u.id,
                  role: u.role,
                })),
              },
            })
          );
        }

        for (const task of taskList) {
          if (!hasRoleChanged) {
            const userRoleMap = new Map(userList.map((e) => [e.id, e.role]));

            hasRoleChanged = (task.members ?? []).some((inv) => {
              const nextRole = userRoleMap.get(inv.user.id);
              return nextRole !== undefined && nextRole !== inv.role;
            });
          }

          const members = task?.members.map((m) => {
            const user = userList.find((u) => m.user.id === u.id);
            if (user) {
              return { userId: user.id, role: user.role };
            }
            return { userId: m.user.id, role: m.role };
          });

          const newMembers = userList
            .filter((u) => !task?.members.find((m) => m.user.id === u.id))
            .map((u) => ({ userId: u.id, role: u.role }));

          if (
            newMembers.length > 0 ||
            hasRoleChanged ||
            members?.length !== task.members.length
          ) {
            promises.push(
              updateTask({
                variables: {
                  id: task.id,
                  attributes: {
                    ...paramValues(task),
                    members: newMembers ? [...members, ...newMembers] : members,
                  },
                },
              })
            );
          }
        }
      }

      if (promises.length > 0) {
        const showFailureToast = (error: unknown) => {
          const toastId = showToast(
            () => {
              setTimeout(() => toast.hide(toastId), 0);
            },
            () => {},
            t('shared:assignmentFailed'),
            getSubmitErrorMessage(error, t),
            undefined,
            true
          );
        };

        let results: any[];
        try {
          results = await Promise.all(promises);
        } catch (err) {
          // A real network-level rejection -- none of these mutations are
          // called with `onError`, so under the client's default
          // errorPolicy: 'all' a genuine network failure still rejects here.
          // A GraphQL business error (e.g. updateTask's blocked-contact
          // PermissionError, TA-10918) instead *resolves* with `errors` set,
          // which this catch never sees -- that's handled below via
          // `mutationFailed` instead.
          showFailureToast(err);
          throw err;
        }

        const failed = results.find(mutationFailed);
        if (failed) {
          // TA-10918: updateTask can now resolve with a PermissionError
          // (e.g. "You blocked this contact.") the same way assignTaskUsers
          // already could, rather than rejecting -- so the try/catch above
          // never sees it, and the success toast below would otherwise fire
          // as if every assignment had gone through even though this one
          // was rolled back server-side. Surface it the same way a real
          // rejection does, and throw so the caller (onAssignMemberPress)
          // keeps the modal open instead of treating this as success.
          const error = toMutationError(
            failed.errors,
            t('shared:exceptionMsg')
          );
          showFailureToast(error);
          throw error;
        }

        const hasInvitations =
          emailList.length > 0 || tasktagUserList.length > 0;

        const toastId = showToast(
          () => {
            // Close toast immediately after click to avoid lingering UI.
            setTimeout(() => toast.hide(toastId), 0);
          },
          () => {},
          hasInvitations ? 'Invite Sent' : 'Member Assigned',
          undefined
        );
      }
    },
    [
      createTaskInvitation,
      updateTask,
      updateTaskMembers,
      paramValues,
      showToast,
      toast,
      tasks,
      taskIdList,
      initialSelectedEmails,
      initialSelectedUsers,
      onAssignMemberFromLastPage,
    ]
  );
  // Users that belong to selected groups (for UI filtering only)
  const groupUserIds = useMemo(() => {
    return new Set(
      selectedGroupList
        .flatMap((g) => (g.users ?? []).map((u: any) => u.id))
        .filter(Boolean)
    );
  }, [selectedGroupList]);

  // Only show users that are not part of any selected group as standalone rows
  const visibleSelectedUsers = useMemo(() => {
    return selectedUserList.filter((u: any) => !groupUserIds.has(u.id));
  }, [selectedUserList, groupUserIds]);

  // Delete members/invitations without refetch: update Apollo cache directly.
  const onDeleteMembers = useCallback(
    async (list: UserSearchType[]) => {
      let doDelFlg = false;
      const taskList = tasks;
      const emailList = list.filter(
        (x) => x.__typename === 'StringSearchType'
      ) as StringSearchType[];

      const ttUserList = list.filter(
        (x) => x.__typename !== 'StringSearchType' && x.isTaskTagUser
      ) as UserSearchType[];

      const userList = list.filter(
        (x) => x.__typename !== 'StringSearchType' && !x.isTaskTagUser
      ) as UserSearchType[];

      // Delete invitations
      if (emailList.length > 0 || ttUserList.length > 0) {
        const emailSet = new Set([
          ...emailList.map((e) => e.title),
          ...ttUserList.map((tu) => tu?.email ?? '-'),
        ]);
        const deletePromises: Promise<any>[] = [];

        for (const task of taskList) {
          const invitations = task.invitations ?? [];
          const userRequests = task.userRequests ?? [];
          if (invitations.length === 0 && userRequests.length === 0) continue;

          const toDelete = invitations.filter((inv) => emailSet.has(inv.email));
          for (const inv of toDelete) {
            deletePromises.push(
              deleteTaskInvitation({
                variables: { taskInvitationId: inv.id },
                optimisticResponse: {
                  deleteTaskInvitation: true,
                },
                update(cache, { data }) {
                  if (!data?.deleteTaskInvitation) {
                    return;
                  }
                  // Remove invitation ref from task.invitations
                  cache.modify({
                    id: cache.identify({ __typename: 'Task', id: task.id }),
                    fields: {
                      invitations(existingRefs = [], { readField }) {
                        return (existingRefs as any[]).filter(
                          (ref) => readField('id', ref) !== inv.id
                        );
                      },
                    },
                  });
                },
              })
            );
          }
          // Delete user request non-contact
          const toDeleteUserReqs = userRequests.filter((req) =>
            emailSet.has(req?.user?.email ?? '-')
          );

          for (const req of toDeleteUserReqs) {
            deletePromises.push(
              deleteUserRequest({
                variables: { id: req.id },
                update(cache, { data }) {
                  if (!data?.deleteUserRequest) {
                    return;
                  }
                  // Remove invitation ref from task.invitations
                  cache.modify({
                    id: cache.identify({ __typename: 'Task', id: task.id }),
                    fields: {
                      userRequests(existingRefs = [], { readField }) {
                        return (existingRefs as any[]).filter(
                          (ref) => readField('id', ref) !== req.id
                        );
                      },
                    },
                  });

                  // Optional: also evict the TaskInvitation object itself if it exists in cache.
                  // cache.evict({ id: cache.identify({ __typename: 'TaskInvitation', id: inv.id }) });
                  // cache.gc();
                },
              })
            );
          }
        }

        if (deletePromises.length > 0) {
          doDelFlg = true;
          await Promise.all(deletePromises);
        }
      }

      // 2) Delete members (excluding Owner)
      if (userList.length > 0) {
        const userIdSet = new Set(userList.map((u) => u.id));
        const updatePromises: Promise<any>[] = [];

        for (const task of taskList) {
          // TA-10633: `updateTask` replaces the whole member set, so every
          // surviving member has to be listed — including the OWNER, which
          // the modal never renders as a chip.
          const nextMembers = buildMembersAfterRemoval(task.members, userIdSet);

          updatePromises.push(
            updateTask({
              variables: {
                id: task.id,
                attributes: {
                  ...paramValues(task),
                  members: nextMembers,
                },
              },
            })
          );
        }

        if (updatePromises.length > 0) {
          doDelFlg = true;
          await Promise.all(updatePromises);
        }
      }
      if (doDelFlg) {
        const toastId = showToast(
          () => {
            // Close toast immediately after click to avoid lingering UI.
            setTimeout(() => toast.hide(toastId), 0);
          },
          () => {},
          t('models:tasks.removeMembers.assigneeDeleted'),
          undefined
        );
      }
    },
    [
      deleteTaskInvitation,
      deleteUserRequest,
      updateTask,
      paramValues,
      onAssignMemberFromLastPage,
      // TA-10633: `tasks` is re-read from the Apollo cache on every render, so
      // it must be a dependency — without it the callback keeps the member
      // list captured on first render and re-sends stale members.
      tasks,
      showToast,
      t,
      toast,
    ]
  );

  // --- Invite link copy ---
  const [copied, setCopied] = useState(false);

  const COPY_LINK_TOAST_KEY = 'copy_link_toast_shown';

  const copyToClipboard = useCallback(
    async (link: string) => {
      if (!link) return;
      await Clipboard.setStringAsync(link);

      setCopied(true);
      setTimeout(() => setCopied(false), 3000);

      const alreadyShown = localStorage.getItem(COPY_LINK_TOAST_KEY);
      if (!alreadyShown) {
        localStorage.setItem(COPY_LINK_TOAST_KEY, '1');
        toast.show(
          <TaskCreatedToast
            title='Link copied'
            message='Paste it to any messaging app to invite your team.'
            duration={3000}
            minWidth={380}
          />,
          {
            type: 'custom',
            duration: 3000,
            placement: 'bottom',
            animationType: 'slide-in',
            style: {
              marginBottom: 39,
              backgroundColor: 'transparent',
              shadowColor: 'transparent',
            },
          }
        );
      }
    },
    [toast]
  );

  const [completeWebTutorialFlows] = useCompleteWebTutorialFlowsMutation();

  const [createInviteMutation, { data: createInviteData }] =
    useCreateInviteMutation();

  useEffect(() => {
    if (!createInviteData?.createInvite) return;
    const formattedLink = createInviteData.createInvite.replace(
      'register/register-with-email',
      'invited-user'
    );
    copyToClipboard(formattedLink);
  }, [createInviteData, copyToClipboard]);

  const doInvite = useCallback(() => {
    // Mark tutorial flow completion only once
    if (!me?.completedWebTutorialFlows?.includes('invite_member')) {
      completeWebTutorialFlows({
        variables: { flowType: WebTutorialFlow.InviteMember },
      });
    }
    if ((taskIdList ?? []).length > 0) {
      createInviteMutation({ variables: { taskId: taskIdList[0] } });
    }
  }, [
    me?.completedWebTutorialFlows,
    completeWebTutorialFlows,
    createInviteMutation,
    taskIdList,
  ]);

  // Fast lookups to prevent duplicates (lowercase for case-insensitive compare)
  const selectedUserIdSet = useMemo(
    () => new Set(selectedUserList.map((u) => (u as any)?.id).filter(Boolean)),
    [selectedUserList]
  );

  const filteredList = useMemo(() => {
    const term = searchVal.trim().toLowerCase();

    // Fall back to the full local contacts list until a gated search has
    // actually settled, so 1-2 character terms don't flash an empty
    // dropdown — TA-10166.
    const base = (hasSearched ? listContacts : (contacts ?? []))
      .filter((u) => !selectedUserIdSet.has(u.id))
      .map(
        (u) =>
          ({ ...u, role: defaultRole, isTaskTagUser: false }) as UserSearchType
      );

    // Inject the current user at the top of contacts when their name/email matches.
    // They are not in the contacts list but should appear here, not in "More on TaskTag".
    // Skip injection if they are already selected.
    if (me && term.length > 0 && !selectedUserIdSet.has(me.id)) {
      const meName = (me.name || '').toLowerCase();
      const meEmail = (me.email || '').toLowerCase();
      if (
        (meName.includes(term) || meEmail.includes(term)) &&
        !base.some((u) => u.id === me.id)
      ) {
        return [
          { ...me, role: defaultRole, isTaskTagUser: false } as UserSearchType,
          ...base,
        ];
      }
    }

    return base;
  }, [
    contacts,
    searchVal,
    hasSearched,
    selectedUserIdSet,
    defaultRole,
    listContacts,
    me,
  ]);

  const filteredTasktagUsers = useMemo(() => {
    const term = searchVal.trim().toLowerCase();

    return listTasktagUsers
      .filter((u) => {
        if (!term) return false;

        // Current user appears in the contacts section instead
        if (u.id === me?.id) return false;

        // Hide users already in contacts to avoid duplicates
        if (contactIdSet.has(u.id)) return false;

        const name = (u.name || '').toLowerCase();
        const email = (u.email || '').toLowerCase();
        return name.includes(term) || email.includes(term);
      })
      .map(
        (u) =>
          ({ ...u, role: defaultRole, isTaskTagUser: true }) as UserSearchType
      );
  }, [listTasktagUsers, searchVal, contactIdSet, defaultRole, me?.id]);

  // Suggest an email invite item if not already present anywhere
  const suggestionList: StringSearchType[] = useMemo(() => {
    if (!searchVal.trim()) return [];

    const semail = getSuggestionEmail(searchVal);
    if (!semail) return [];

    const semailLower = semail.toLowerCase();

    const inContacts = filteredList.some(
      (t: any) => (t.email || '').toLowerCase() === semailLower
    );
    if (inContacts) return [];

    const inTasktagUsers = filteredTasktagUsers.some(
      (t: any) => (t.email || '').toLowerCase() === semailLower
    );
    if (inTasktagUsers) return [];

    const inGroups = listGroup.some((g) =>
      (g.users ?? []).some((u) => (u.email || '').toLowerCase() === semailLower)
    );
    if (inGroups) return [];

    return [
      { title: semail, role: defaultRole, __typename: 'StringSearchType' },
    ];
  }, [
    searchVal,
    getSuggestionEmail,
    filteredList,
    filteredTasktagUsers,
    listGroup,
    selectedEmailList,
    defaultRole,
  ]);

  // Build a display name for a User
  // --- Render Helpers ---
  const renderUser = useCallback(
    (item: any, showText: boolean = true) => {
      if (item.__typename === 'str') {
        return (
          <Text
            variant='webBodySecondary'
            color='textPrimary'
            flexShrink={1}
            ellipsizeMode='tail'
            numberOfLines={1}>
            More on TaskTag
          </Text>
        );
      }

      if (item.__typename === 'StringSearchType') {
        return (
          <Box
            key={item.title}
            flex={1}
            flexDirection='row'
            alignItems='center'>
            <EmailAvatar width={40} />
            <Box mx='xs'>
              <Text
                variant='webBodySecondary'
                color='textPrimary'
                width={500}
                flexShrink={1}
                ellipsizeMode='tail'
                numberOfLines={1}>
                {showText && <Text>{'Invite: '}</Text>}
                <Highlighter
                  autoEscape
                  highlightStyle={{
                    backgroundColor: theme.colors.yellowBright,
                  }}
                  searchWords={[searchVal]}
                  textToHighlight={item.title}
                />
              </Text>
              {!showText &&
                (taskIdList?.length ?? 0) === 1 &&
                item.expiredAt && (
                  <Text
                    variant='webBodyHintSubline'
                    color='grey04'
                    flexShrink={1}
                    marginTop='xxs'
                    ellipsizeMode='tail'
                    numberOfLines={1}>
                    {'Invite expired ' + format(item.expiredAt, 'MMM dd, yyyy')}
                  </Text>
                )}
            </Box>
          </Box>
        );
      }

      const user = item as User;
      const isSelf = user.id === me?.id;
      const realName = getDisplayName(user);

      return (
        <Box key={user.id} flex={1} flexDirection='row' alignItems='center'>
          <Avatar
            disableAvatarPress={true}
            size='medium'
            id={user.id}
            avatar={user.avatar}
            label={realName}
            isMultiple={false}
          />
          <Box mx='xs'>
            <Text
              width={500}
              variant='webBodySecondary'
              color='textPrimary'
              flexShrink={1}
              ellipsizeMode='tail'
              numberOfLines={1}>
              <Highlighter
                autoEscape
                highlightStyle={{ backgroundColor: theme.colors.yellowBright }}
                searchWords={[searchVal]}
                textToHighlight={realName}
              />
              {isSelf && ' (You)'}
            </Text>

            {!isSelf && !!user.email && (
              <Text
                mr='m'
                width={500}
                flexShrink={1}
                variant='webBodyHintSubline'
                color='grey04'
                marginTop='xxs'
                ellipsizeMode='tail'
                numberOfLines={1}>
                <Highlighter
                  autoEscape
                  highlightStyle={{
                    backgroundColor: theme.colors.yellowBright,
                  }}
                  searchWords={[searchVal]}
                  textToHighlight={user.email}
                />
              </Text>
            )}
          </Box>
        </Box>
      );
    },
    [searchVal, me?.id]
  );

  // Add a single user into selectedUserList (deduplicated)
  const addUserToSelected = useCallback(
    (user: User) => {
      setSelectedUserList((prev) => {
        if (prev.some((u: any) => u.id === user.id)) return prev;
        return [
          ...prev,
          { ...(user as User), role: defaultRole } as UserSearchType,
        ];
      });
    },
    [defaultRole]
  );

  const addEmailToSelected = useCallback(
    (emailItem: StringSearchType) => {
      setSelectedEmailList((prev) => {
        if (
          prev.some(
            (e) => e.title.toLowerCase() === emailItem.title.toLowerCase()
          )
        )
          return prev;
        return [...prev, { ...emailItem, role: emailItem.role ?? defaultRole }];
      });
    },
    [defaultRole]
  );

  // Add a group into selectedGroupList:
  // - Add group entry (with users)
  // - Remove those users from standalone selectedUserList
  const addGroupToSelected = useCallback(
    (group: Group) => {
      const groupUsers: UserSearchType[] = (group.users ?? []).map((u) => ({
        ...(u as User),
        role: defaultRole,
      }));

      setSelectedGroupList((prev) => {
        if (prev.some((g) => g.id === group.id)) return prev;
        return [...prev, { ...group, users: groupUsers } as SelectedGroupType];
      });

      const groupUserIds = new Set(groupUsers.map((u: any) => u.id));
      setSelectedUserList((prev) =>
        prev.filter((u: any) => !groupUserIds.has(u.id))
      );

      // Expand by default in assign panel
      if (!expandedGroupIds.includes(group.id)) {
        toggleGroupExpanded(group.id);
      }
    },
    [defaultRole, expandedGroupIds, toggleGroupExpanded]
  );

  // Shared key extractor (stable)
  const keyExtractor = useCallback((item: any) => {
    if (item.__typename === 'StringSearchType') return `email:${item.title}`;
    if (item.__typename === 'str') return 'more-on-tasktag';
    if (item.__typename === 'Group') return `group:${item.id}`;
    return `user:${item.id}`;
  }, []);

  // --- Dropdown Render (search results) ---
  const renderDropdownItem = useCallback(
    ({ item }: ListRenderItemInfo<any>) => {
      // Group handling in dropdown:
      // - Chevron: expand/collapse only
      // - Row click: select group
      if (item.__typename === 'Group') {
        const group = item as Group;
        const isExpanded = !searchExpandedGroupIds.includes(group.id);

        return (
          <SearchGroupRow
            group={group}
            isExpanded={isExpanded}
            searchVal={searchVal}
            selectedUser={selectedUser}
            onToggleExpand={() => toggleSearchGroupExpanded(group.id)}
            onSelectGroup={() => {
              addGroupToSelected(group);
              setText('');
              setFocusOnSearch(false);
            }}
            onSelectMember={(gu) => {
              addUserToSelected(gu);
              setText('');
              setFocusOnSearch(false);
            }}
            renderUser={renderUser}
            alreadyAddedLabel={t('shared:alreadyAdded')}
          />
        );
      }

      // Non-group dropdown items
      let accessibilityLabel = 'Add Assignee';
      if (item.__typename === 'StringSearchType') {
        accessibilityLabel = `Add Assignee | ${item.title}`;
      } else if (item.__typename === 'User') {
        accessibilityLabel = `Add Assignee | ${getDisplayName(item as User)}`;
      }

      return (
        <SimpleHoverable
          accessibilityLabel={accessibilityLabel}
          onPress={() => {
            if (item.__typename === 'StringSearchType')
              addEmailToSelected(item as StringSearchType);
            if (item.__typename === 'User') addUserToSelected(item as User);
            if (item.__typename === 'str') return;
            // "str" is a visual entry; no selection action currently

            setText('');
            setFocusOnSearch(false);
          }}>
          {(hover) => (
            <Box
              flex={1}
              px='m'
              py='s'
              flexDirection='row'
              alignItems='center'
              borderBottomColor='grey02'
              borderBottomWidth={1}
              style={{
                backgroundColor:
                  hover && item.__typename !== 'str' ? '#FAFBFC' : 'white',
              }}
              justifyContent='center'>
              <Box flex={1}>{renderUser(item)}</Box>

              {selectedUser.some(
                (t) =>
                  (t?.__typename === 'User' && t?.email === item.email) ||
                  (t?.__typename !== 'User' && t?.title === item?.title)
              ) && (
                <Text
                  flexShrink={1}
                  variant='webBodyHintSubline'
                  color='grey04'
                  marginTop='xxs'
                  ellipsizeMode='tail'
                  numberOfLines={1}>
                  {t('shared:alreadyAdded')}
                </Text>
              )}
            </Box>
          )}
        </SimpleHoverable>
      );
    },
    [
      addEmailToSelected,
      addGroupToSelected,
      addUserToSelected,
      renderUser,
      searchExpandedGroupIds,
      searchVal,
      toggleSearchGroupExpanded,
    ]
  );

  // --- Assign Panel Render (selected items) ---
  const updateRoleForSelectedUser = useCallback(
    (userId: string, role: TaskMemberRole) => {
      setSelectedUserList((prev) =>
        prev.map((u: any) => (u.id === userId ? { ...u, role } : u))
      );
    },
    []
  );

  const updateRoleForSelectedEmail = useCallback(
    (title: string, role: TaskMemberRole) => {
      const k = title.toLowerCase();
      setSelectedEmailList((prev) =>
        prev.map((e) => (e.title.toLowerCase() === k ? { ...e, role } : e))
      );
    },
    []
  );

  const updateRoleForGroupUser = useCallback(
    (groupId: string, userId: string, role: TaskMemberRole) => {
      setSelectedGroupList((prev) =>
        prev.map((g) => {
          if (g.id !== groupId) return g;
          const nextUsers = (g.users ?? []).map((u: any) =>
            u.id === userId ? { ...u, role } : u
          );
          return { ...g, users: nextUsers };
        })
      );
    },
    []
  );

  // TA-10633: the removal handlers below used to run their side effects
  // (the delete mutation + notifyParent) *inside* a setState updater. React
  // treats updaters as pure: StrictMode invokes them twice, a re-render can
  // replay them, and an updater queued on a component that unmounts before
  // the next render never runs at all — so the delete fired an unpredictable
  // number of times, including zero. A zero-run looks exactly like the
  // reported bug: no "Assignee Deleted" toast, no mutation, member still
  // there after a refresh. The current list comes from the ref instead, which
  // the effects above keep in sync, and the ref is advanced eagerly so two
  // removals in the same tick still see each other.
  const removeSelectedUser = useCallback(
    (userId: string) => {
      const prev = selectedUserListRef.current;
      const removed = prev.filter((u: any) => u.id === userId);
      if (removed.length === 0) return;

      const next = prev.filter((u: any) => u.id !== userId);
      selectedUserListRef.current = next;
      setSelectedUserList(next);

      onDeleteMembers(removed);

      // Send full merged list back to parent (must include group users + emails)
      notifyParent(
        buildMergedSelected(
          next,
          selectedGroupListRef.current,
          selectedEmailListRef.current
        )
      );
    },
    [onDeleteMembers, notifyParent, buildMergedSelected]
  );

  const removeSelectedEmail = useCallback(
    (title: string) => {
      const k = title.toLowerCase();
      const prev = selectedEmailListRef.current;
      const removed = prev.filter((e) => e.title.toLowerCase() === k);
      if (removed.length === 0) return;

      const next = prev.filter((e) => e.title.toLowerCase() !== k);
      selectedEmailListRef.current = next;
      setSelectedEmailList(next);

      onDeleteMembers(removed);

      // Send full merged list back to parent (must include group users + standalone users)
      notifyParent(
        buildMergedSelected(
          selectedUserListRef.current,
          selectedGroupListRef.current,
          next
        )
      );
    },
    [onDeleteMembers, notifyParent, buildMergedSelected]
  );

  const removeSelectedUserInGroup = useCallback(
    (groupId: string, userId: string) => {
      // Find the removed member object from current group list (NOT from selectedUserList)
      const currentGroup = selectedGroupListRef.current.find(
        (g) => g.id === groupId
      );
      const removedMembers = (currentGroup?.users ?? []).filter(
        (u: any) => u.id === userId
      );

      // 1) Update group list. Same rule as removeSelectedUser (TA-10633):
      // notifyParent is a side effect, so it stays out of the updater.
      const nextGroups: SelectedGroupType[] = selectedGroupListRef.current
        .map((g): SelectedGroupType => {
          if (g.id !== groupId) return g;
          const nextUsers = (g.users ?? []).filter((u: any) => u.id !== userId);
          return { ...g, users: nextUsers };
        })
        // If a group's users becomes empty, remove the group row from UI
        .filter((g) => (g.users?.length ?? 0) > 0);

      selectedGroupListRef.current = nextGroups;
      setSelectedGroupList(nextGroups);

      // 2) Perform server-side removal
      // Use removed member object from group (not from selectedUserList, since group users were removed from it)
      if (removedMembers.length > 0) {
        onDeleteMembers(removedMembers);
      }

      // 3) Keep selectedUserList in sync if (and only if) you allow group members to be added as standalone
      // In your current logic, group users are removed from selectedUserList when selecting a group,
      // so this is usually a no-op, but keeping it is safe.
      const nextUsers = selectedUserListRef.current.filter(
        (u: any) => u.id !== userId
      );
      selectedUserListRef.current = nextUsers;
      setSelectedUserList(nextUsers);

      // IMPORTANT:
      // Send full merged list back to parent, including updated group users.
      // Otherwise parent will drop group users -> props change -> your effect clears groups.
      notifyParent(
        buildMergedSelected(nextUsers, nextGroups, selectedEmailListRef.current)
      );
    },
    [onDeleteMembers, notifyParent, buildMergedSelected]
  );

  const removeSelectedGroup = useCallback(
    (groupId: string) => {
      // Collapse group if it's expanded before removing it
      if (expandedGroupIds.includes(groupId)) {
        toggleGroupExpanded(groupId);
      }

      // Same rule as removeSelectedUser (TA-10633): the delete mutation and
      // notifyParent are side effects and must not run inside the updater.
      const prevGroups = selectedGroupListRef.current;
      const group = prevGroups.find((g) => g.id === groupId);
      const ids = new Set((group?.users ?? []).map((u: any) => u.id));

      const nextGroups = prevGroups.filter((g) => g.id !== groupId);
      selectedGroupListRef.current = nextGroups;
      setSelectedGroupList(nextGroups);

      // Remove the group's users from standalone selectedUserList if they exist there
      const nextUsers = selectedUserListRef.current.filter(
        (u: any) => !ids.has(u.id)
      );

      if (ids.size > 0) {
        selectedUserListRef.current = nextUsers;
        setSelectedUserList(nextUsers);

        // Optional: if you want to call delete API when removing the group row,
        // you should delete by group.users, not by selectedUserList.
        const removed = (group?.users ?? []).filter((u: any) => ids.has(u.id));
        if (removed.length > 0) onDeleteMembers(removed);
      }

      // Notify parent with the full merged list after removing group
      notifyParent(
        buildMergedSelected(nextUsers, nextGroups, selectedEmailListRef.current)
      );
    },
    [
      onDeleteMembers,
      notifyParent,
      buildMergedSelected,
      expandedGroupIds,
      toggleGroupExpanded,
    ]
  );

  const renderAssignItem = useCallback(
    ({ item }: ListRenderItemInfo<any>) => {
      const isGroup = item.__typename === 'Group';
      const isStringType = item.__typename === 'StringSearchType';

      // Group row in assign panel
      if (isGroup) {
        const group = item as SelectedGroupType;
        const groupUsers = group.users ?? [];
        const isExpanded = expandedGroupIds.includes(group.id);

        return (
          <Box key={`group-row:${group.id}`}>
            <SimpleHoverable
              accessibilityLabel={`${
                isExpanded ? 'Collapse' : 'Expand'
              } Group | ${group.name}`}
              onPress={() => toggleGroupExpanded(group.id)}>
              {(hover) => (
                <Box
                  flex={1}
                  px='xs'
                  py='s'
                  borderBottomColor='grey02'
                  borderBottomWidth={1}
                  flexDirection='row'
                  backgroundColor={hover ? 'grey01' : 'white'}
                  alignItems='center'>
                  <Box flex={1} flexDirection='row' alignItems='center'>
                    <Icon
                      name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                      variant='l'
                      marginHorizontal='xs'
                      color='grey05'
                    />
                    <Box ml='xs'>
                      <Text
                        variant='webBodySecondary'
                        color='textPrimary'
                        numberOfLines={1}>
                        {group.name}
                      </Text>
                      <Text
                        variant='webBodyHintSubline'
                        color='grey04'
                        marginTop='xxs'
                        numberOfLines={1}>
                        {`${groupUsers.length} members`}
                      </Text>
                    </Box>
                  </Box>

                  <TouchableOpacity
                    accessibilityLabel={`Remove Group | ${group.name}`}
                    onPress={() => removeSelectedGroup(group.id)}>
                    <Box
                      ml='xs'
                      width={32}
                      height={32}
                      alignItems='center'
                      justifyContent='center'>
                      <Icon color='grey05' variant='s' name='Trash2' />
                    </Box>
                  </TouchableOpacity>
                </Box>
              )}
            </SimpleHoverable>

            {isExpanded &&
              groupUsers.map((member: any) => (
                <AssignGroupMemberRow
                  key={`${group.id}:${member.id}`}
                  member={member}
                  groupId={group.id}
                  canEditTaskTopLevel={canEditTaskTopLevel}
                  isProjectEditor={isProjectEditor}
                  isTeamMember={isTeamMember}
                  meId={me?.id}
                  onRoleChange={(memberId, role) => {
                    updateRoleForGroupUser(group.id, memberId, role);
                    updateRoleForSelectedUser(memberId, role);
                  }}
                  onRemove={removeSelectedUserInGroup}
                  renderUser={renderUser}
                />
              ))}
          </Box>
        );
      }

      // Single user / email row
      const role = item.role as TaskMemberRole;
      // TA-9491: editors and team members must be able to change roles too,
      // so the role dropdown shouldn't be disabled for them.
      const disabledRole =
        (role === TaskMemberRole.Assignee && !canManageRoles) ||
        item.id === me?.id;

      return (
        <Box
          key={isStringType ? `email-row:${item.title}` : `user-row:${item.id}`}
          flex={1}
          px='xs'
          py='s'
          borderBottomColor='grey02'
          borderBottomWidth={1}
          flexDirection='row'
          alignItems='center'>
          <Box flex={1}>{renderUser(item, false)}</Box>

          <BaseButtonV3
            accessibilityLabel='Assign button'
            noBorder={true}
            noRadius={false}
            noHover={false}
            buttonRadius='xs'
            modalRadius={8}
            showHoverWhenDropdown={true}
            disabled={disabledRole}
            mr='s'
            popModalComponent={({ onClose: closeRoleModal }) => (
              <TaskRoleSelectorWeb
                isOwner={canEditTaskTopLevel}
                isProjectEditor={isProjectEditor}
                isTeamMember={isTeamMember}
                selectedRole={role}
                onRoleSelect={(nextRole) => {
                  if (isStringType)
                    updateRoleForSelectedEmail(item.title, nextRole);
                  else updateRoleForSelectedUser(item.id, nextRole);
                  closeRoleModal();
                }}
              />
            )}>
            <Box
              width={86}
              mx='m'
              alignItems='center'
              justifyContent='flex-end'
              flexDirection='row'>
              <Text variant='webSmall' color='textPrimary' marginRight='xs'>
                {role === TaskMemberRole.Assignee ? 'Assignee' : 'Viewer'}
              </Text>
              {!disabledRole && (
                <Icon color='grey05' variant='s' name='ChevronDown' />
              )}
            </Box>
          </BaseButtonV3>

          {(!disabledRole || isProjectEditor || isTeamMember) && (
            <SimpleHoverable
              accessibilityLabel={`Remove Member | ${getDisplayName(item)}`}
              onPress={() => {
                if (isStringType) removeSelectedEmail(item.title);
                else {
                  removeSelectedUser(item.id);
                }
              }}>
              {(hover) => (
                <Box
                  width={32}
                  height={32}
                  borderRadius='xs'
                  backgroundColor={hover ? 'grey01' : 'white'}
                  alignItems='center'
                  justifyContent='center'>
                  <Icon
                    color={hover ? 'alertRed' : 'grey05'}
                    variant='s'
                    name='Trash2'
                  />
                </Box>
              )}
            </SimpleHoverable>
          )}
        </Box>
      );
    },
    [
      canEditTaskTopLevel,
      canManageRoles,
      expandedGroupIds,
      isProjectEditor,
      isTeamMember,
      me?.id,
      removeSelectedEmail,
      removeSelectedGroup,
      removeSelectedUser,
      removeSelectedUserInGroup,
      renderUser,
      toggleGroupExpanded,
      updateRoleForGroupUser,
      updateRoleForSelectedEmail,
      updateRoleForSelectedUser,
    ]
  );

  // Build dropdown data once (avoid recreating array in render)
  const dropdownData = useMemo(() => {
    return [
      ...suggestionList,
      ...filteredList,
      ...listGroup,
      // Hide More on TaskTag
      ...(filteredTasktagUsers.length > 0
        ? [{ name: 'More on TaskTag', __typename: 'str' }]
        : []),
      ...filteredTasktagUsers,
    ];
  }, [suggestionList, filteredList, listGroup, filteredTasktagUsers]);

  // Disable assign button when nothing is selected
  const assignDisabled = useMemo(() => {
    const changed = selectedUserList.some(
      (su) =>
        !initialSelectedUsers.some(
          (iu) => iu.id === su.id && iu.role === su.role
        )
    );

    if (changed) return false;

    const changed1 = selectedGroupList.length > 0;

    if (changed1) return false;

    const emailChanged = selectedEmailList.some(
      (su) =>
        !initialSelectedEmails.some(
          (iu) => iu.title === su.title && iu.role === su.role
        )
    );

    if (emailChanged) return false;

    return true;
  }, [
    selectedUserList,
    selectedEmailList,
    selectedGroupList,
    initialSelectedEmails,
    initialSelectedUsers,
  ]);

  const [isAssigning, setIsAssigning] = useState(false);

  const onPressAssign = useCallback(async () => {
    setIsAssigning(true);

    if (assignDisabled) {
      const toastId = showToast(
        () => {
          // Close toast immediately after click to avoid lingering UI.
          setTimeout(() => toast.hide(toastId), 0);
        },
        () => {},
        'Invite Sent',
        undefined
      );
      onClose?.();
      setIsAssigning(false);
      return;
    }
    // Merge standalone users + group users + emails (dedup by id/title)
    const usersFromGroups: UserSearchType[] = selectedGroupList.flatMap(
      (g) => g.users ?? []
    );

    const merged: UserSearchType[] = [];
    const seen = new Set<string>();

    // Users
    [...selectedUserList, ...usersFromGroups].forEach((u: any) => {
      const key = `user:${u.id}`;
      if (u?.id && !seen.has(key)) {
        seen.add(key);
        merged.push(u);
      }
    });

    // Emails
    selectedEmailList.forEach((e) => {
      const key = `email:${e.title.toLowerCase()}`;
      if (!seen.has(key)) {
        seen.add(key);
        merged.push(e);
      }
    });

    try {
      await onAssignMember(merged);
      onClose?.();
    } catch {
      // onAssignMember already surfaced the failure via its own toast (e.g.
      // TA-10918's blocked-contact PermissionError) -- swallow here just to
      // keep the modal open rather than closing it and to avoid this
      // becoming a second, unhandled rejection on top of that toast.
    } finally {
      setIsAssigning(false);
    }
  }, [
    onAssignMember,
    onClose,
    selectedEmailList,
    selectedGroupList,
    selectedUserList,
    assignDisabled,
  ]);

  return (
    <Box
      py='xs'
      paddingHorizontal='m'
      justifyContent='flex-start'
      width={800}
      position='relative'>
      {/* Copy-to-clipboard toast */}
      {!copied && isHoveredCopyLinkToInvite && (
        <Box
          position='absolute'
          top={48}
          right={56}
          zIndex={9999}
          pointerEvents='none'>
          <Box
            backgroundColor='textPrimary'
            // borderWidth={1}
            // borderColor='grey02'
            borderRadius='xxs'
            minWidth={129}
            flexDirection='row'
            justifyContent='center'
            alignItems='center'
            py='xs'
            px='s'
            style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
            {/*<Icon name='CheckCircle' color='greenSecondary' variant='s' />*/}
            <Text
              ml='xs'
              accessibilityLabel='Copy link'
              variant='labelSmall'
              color='white'>
              Copy link to invite
            </Text>
          </Box>
        </Box>
      )}
      {copied && (
        <Box
          position='absolute'
          top={48}
          right={56}
          zIndex={9999}
          pointerEvents='none'>
          <Box
            backgroundColor='white'
            borderWidth={1}
            borderColor='grey02'
            borderRadius='xxs'
            minWidth={129}
            flexDirection='row'
            justifyContent='center'
            alignItems='center'
            py='xs'
            px='s'
            style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
            <Icon name='CheckCircle' color='greenSecondary' variant='s' />
            <Text
              ml='xs'
              accessibilityLabel='Copy link'
              variant='labelSmall'
              color='black'>
              Link copied!
            </Text>
          </Box>
        </Box>
      )}

      {/* Header */}
      <Box flexDirection='row' mt='m' alignItems='center'>
        <Text flex={1} variant='labelLarge' color='textPrimary'>
          Invite or Add Assignee
        </Text>

        {(taskIdList ?? []).length === 1 && (
          <Box
            flexDirection='row'
            alignItems='center'
            justifyContent='center'
            mr='m'>
            <Box
              borderRadius='xs'
              px='m'
              py='xxs'
              mx='xxs'
              mt='xxxs'
              backgroundColor={isHoveredCopyLinkToInvite ? 'grey01' : undefined}
              onMouseEnter={() => setIsHoveredCopyLinkToInvite(true)}
              onMouseLeave={() => setIsHoveredCopyLinkToInvite(false)}>
              <TouchableOpacity
                accessibilityLabel='Copy Link'
                onPress={doInvite}
                activeOpacity={1}>
                <Box
                  backgroundColor={
                    isHoveredCopyLinkToInvite ? 'grey01' : undefined
                  }
                  flexDirection='row'
                  justifyContent='center'>
                  <Icon color='blue' variant='s' name='Link' marginRight='xs' />
                  <Text flex={1} variant='labelSmall' color='blue'>
                    Copy Link
                  </Text>
                </Box>
              </TouchableOpacity>
            </Box>
          </Box>
        )}

        <WebIcon
          accessibilityLabel='Close'
          name='X'
          variant='l'
          color='grey05'
          onPress={onClose}
        />
      </Box>

      {/* Search input */}
      <Box mt='s'>
        <AddMembersInput
          focusOnSearch={focusOnSearch}
          setFocusOnSearch={setFocusOnSearch}
          searchVal={searchVal}
          setText={setText}
          inputRef={inputRef}
        />
      </Box>

      {/* Dropdown for search results */}
      {shouldShowDropdown && (
        <Box
          position='absolute'
          // Keep your existing positioning; you can replace with measured layout if needed
          top={64 + 16 + 48 + 4}
          left={16}
          right={16}
          borderRadius='xs'
          borderWidth={1}
          borderColor='grey02'
          backgroundColor='white'
          style={{
            maxHeight: 440,
            minHeight: 180,
            overflowY: 'auto',
            overflowX: 'hidden',
            zIndex: 10000,
            boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
          }}>
          <FlatList
            keyExtractor={keyExtractor}
            keyboardShouldPersistTaps='handled'
            data={dropdownData}
            renderItem={renderDropdownItem}
            ListEmptyComponent={() => <Box />}
            contentContainerStyle={{ paddingVertical: 4 }}
          />
        </Box>
      )}

      {/* Empty state */}
      {selectedUserList.length === 0 &&
        selectedEmailList.length === 0 &&
        selectedGroupList.length === 0 && (
          <Box mt='s' alignItems='center'>
            <Image
              style={{ width: 86, height: 86 }}
              source={require('../../../assets/images/teammates.png')}
            />
            <Text
              mt='xs'
              mb='xxxs'
              flex={1}
              textAlign='center'
              variant='webBodySecondary'
              color='textPrimary'>
              Assign teammates to this task
            </Text>
            <Text
              my='xs'
              flex={1}
              textAlign='center'
              variant='webBodyHintSubline'
              color='grey04'>
              Add existing assignees or invite new ones to collaborate on this
              task.
            </Text>
          </Box>
        )}

      {/* Assign panel: emails + groups + standalone users */}
      {!(
        selectedUserList.length === 0 &&
        selectedEmailList.length === 0 &&
        selectedGroupList.length === 0
      ) && (
        <Box minHeight={56} maxHeight={56 * 8}>
          <FlatList
            keyExtractor={keyExtractor}
            keyboardShouldPersistTaps='handled'
            data={[
              ...selectedEmailList,
              ...selectedGroupList,
              ...visibleSelectedUsers,
            ]}
            renderItem={renderAssignItem}
            ListEmptyComponent={() => <Box />}
            contentContainerStyle={{ paddingVertical: 4 }}
          />
        </Box>
      )}

      {/* Assign button */}
      <Button
        disabled={
          (selectedEmailList.length === 0 &&
            selectedGroupList.length === 0 &&
            selectedUserList.length === 0) ||
          isAssigning
        }
        onPress={onPressAssign}
        height={48}
        fullWidth
        backgroundColor={
          (selectedEmailList.length === 0 &&
            selectedGroupList.length === 0 &&
            selectedUserList.length === 0) ||
          isAssigning
            ? 'grey02'
            : 'grey05'
        }
        marginTop='m'
        marginBottom='xxxs'
        accessibilityLabel='Assign'>
        {isAssigning ? (
          <Box height={32}>
            <ActivityIndicatorLoading color='white' />
          </Box>
        ) : (
          <Text
            variant='labelSmall'
            color={
              selectedEmailList.length === 0 &&
              selectedGroupList.length === 0 &&
              selectedUserList.length === 0
                ? 'grey04'
                : 'white'
            }>
            Assign
          </Text>
        )}
      </Button>

      {/* Footer note */}
      <Box
        mt='xs'
        height={16}
        mb='m'
        flexDirection='row'
        justifyContent='center'
        alignItems='center'>
        <Icon color='grey04' variant='s' name='Pending' />
        <Text
          ml='xxs'
          textAlign='center'
          variant='webBodyHintSubline'
          color='grey04'>
          Invitations expire after 7 days
        </Text>
      </Box>
    </Box>
  );
};
